'use client';

import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import Image from 'next/image';

interface TreatmentGalleryProps {
  images: { before: string; after: string; caption: string }[];
}

export default function TreatmentGallery({ images }: TreatmentGalleryProps) {
  const [activeTab, setActiveTab] = useState('side-by-side');
  const [comparePositions, setComparePositions] = useState<number[]>(
    images.map(() => 50)
  );
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [hoveringIndex, setHoveringIndex] = useState<number | null>(null);
  const containerRefs = useRef<(HTMLDivElement | null)[]>([]);

  const updatePosition = useCallback((clientX: number, imageIndex: number) => {
    const container = containerRefs.current[imageIndex];
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    const position = ((clientX - containerRect.left) / containerRect.width) * 100;
    const clampedPosition = Math.max(0, Math.min(100, position));

    setComparePositions(prev => {
      const newPositions = [...prev];
      newPositions[imageIndex] = clampedPosition;
      return newPositions;
    });
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent, imageIndex: number) => {
    if (hoveringIndex !== imageIndex) return;
    updatePosition(e.clientX, imageIndex);
  }, [hoveringIndex, updatePosition]);

  const handleMouseEnter = useCallback((imageIndex: number) => {
    setHoveringIndex(imageIndex);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveringIndex(null);
  }, []);

  // Reset comparison positions when switching tabs
  useEffect(() => {
    setComparePositions(images.map(() => 50));
  }, [activeTab, images]);

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="side-by-side">Side by Side</TabsTrigger>
          <TabsTrigger value="before-after">Before & After</TabsTrigger>
          <TabsTrigger value="slider">Comparison Slider</TabsTrigger>
        </TabsList>

        <TabsContent value="side-by-side" className="mt-4">
          <Carousel aria-label="Side by side image gallery">
            <CarouselContent>
              {images.map((image, index) => (
                <CarouselItem key={index}>
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="overflow-hidden">
                      <div className="relative h-64">
                        <Image
                          src={image.before}
                          alt={`Before ${image.caption}`}
                          fill={true}
                          style={{ objectFit: "cover" }}
                        />
                        <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded-sm">
                          Before
                        </div>
                      </div>
                    </Card>
                    <Card className="overflow-hidden">
                      <div className="relative h-64">
                        <Image
                          src={image.after}
                          alt={`After ${image.caption}`}
                          fill={true}
                          style={{ objectFit: "cover" }}
                        />
                        <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded-sm">
                          After
                        </div>
                      </div>
                    </Card>
                  </div>
                  <p className="text-center mt-2 text-gray-600">{image.caption}</p>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </TabsContent>

        <TabsContent value="before-after" className="mt-4">
          <Carousel aria-label="Before and after image toggle">
            <CarouselContent>
              {images.map((image, index) => (
                <CarouselItem key={index}>
                  <Card className="overflow-hidden">
                    <div className="relative h-80">
                      <Image
                        src={activeImageIndex === index ? image.after : image.before}
                        alt={activeImageIndex === index ? `After ${image.caption}` : `Before ${image.caption}`}
                        fill={true}
                        style={{ objectFit: "cover" }}
                      />
                      <div className={`absolute top-2 left-2 text-white text-xs px-2 py-1 rounded-sm ${activeImageIndex === index ? 'bg-green-500' : 'bg-red-500'
                        }`}>
                        {activeImageIndex === index ? 'After' : 'Before'}
                      </div>
                    </div>
                    <div className="p-4 flex justify-center space-x-4">
                      <Button
                        variant={activeImageIndex === index ? "outline" : "default"}
                        onClick={() => setActiveImageIndex(index !== activeImageIndex ? index : -1)}
                        aria-pressed={activeImageIndex === index}
                      >
                        {activeImageIndex === index ? 'View Before' : 'View After'}
                      </Button>
                    </div>
                  </Card>
                  <p className="text-center mt-2 text-gray-600">{image.caption}</p>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </TabsContent>

        <TabsContent value="slider" className="mt-4">
          <Carousel aria-label="Interactive image comparison slider">
            <CarouselContent>
              {images.map((image, index) => (
                <CarouselItem key={index}>
                  <Card className="overflow-hidden">
                    <div
                      ref={(el) => {
                        containerRefs.current[index] = el;
                      }}
                      role="slider"
                      aria-orientation="horizontal"
                      aria-valuenow={Math.round(comparePositions[index] || 50)}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label={`Image comparison slider for ${image.caption}. Move mouse over image to compare before and after.`}
                      tabIndex={0}
                      className="relative h-80 select-none cursor-none focus:outline-hidden focus:ring-2 focus:ring-primary"
                      onMouseMove={(e) => handleMouseMove(e, index)}
                      onMouseEnter={() => handleMouseEnter(index)}
                      onMouseLeave={handleMouseLeave}
                      onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                        let newPosition = comparePositions[index] || 50;
                        const step = e.shiftKey ? 10 : 2;

                        switch (e.key) {
                          case 'ArrowLeft':
                            newPosition = Math.max(0, newPosition - step);
                            break;
                          case 'ArrowRight':
                            newPosition = Math.min(100, newPosition + step);
                            break;
                          case 'Home':
                            newPosition = 0;
                            break;
                          case 'End':
                            newPosition = 100;
                            break;
                          default:
                            return;
                        }

                        if (newPosition !== comparePositions[index]) {
                          setComparePositions(prev => {
                            const newPositions = [...prev];
                            newPositions[index] = newPosition;
                            return newPositions;
                          });
                          e.preventDefault();
                        }
                      }}
                    >
                      {/* After image (background) */}
                      <Image
                        src={image.after}
                        alt={`After ${image.caption}`}
                        fill={true}
                        style={{ objectFit: "cover" }}
                        className="absolute top-0 left-0"
                        priority
                      />
                      {/* After label - disappears when slider is at far right */}
                      {(comparePositions[index] || 50) < 95 && (
                        <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-sm z-20">
                          After
                        </div>
                      )}

                      {/* Before image (clipped overlay) */}
                      <div
                        className="absolute inset-0 overflow-hidden"
                        style={{ clipPath: `polygon(0 0, ${comparePositions[index] || 50}% 0, ${comparePositions[index] || 50}% 100%, 0 100%)` }}
                      >
                        <Image
                          src={image.before}
                          alt={`Before ${image.caption}`}
                          fill={true}
                          style={{ objectFit: "cover" }}
                          className="absolute top-0 left-0"
                          priority
                        />
                        {/* Before label - disappears when slider is at far left */}
                        {(comparePositions[index] || 50) > 5 && (
                          <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded-sm z-20">
                            Before
                          </div>
                        )}
                      </div>

                      {/* Slider control */}
                      <div
                        className="absolute top-0 bottom-0 w-0.5 bg-white z-30 pointer-events-none"
                        style={{ left: `${comparePositions[index] || 50}%` }}
                      >
                        <motion.div
                          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center pointer-events-none border-2 border-gray-200"
                          animate={{
                            scale: hoveringIndex === index ? 1.1 : 1,
                            boxShadow: hoveringIndex === index ? "0px 0px 20px rgba(0,0,0,0.3)" : "0px 4px 12px rgba(0,0,0,0.15)"
                          }}
                          transition={{ duration: 0.2 }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-gray-600"
                          >
                            <path d="M18 8L22 12L18 16" />
                            <path d="M6 8L2 12L6 16" />
                            <path d="M2 12H22" />
                          </svg>
                        </motion.div>
                      </div>

                      {/* Instructional text */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white text-sm px-3 py-1 rounded-full transition-opacity z-20 pointer-events-none">
                        {hoveringIndex === index ? `${Math.round(comparePositions[index] || 50)}% comparison` : 'Hover to compare • Use arrow keys'}
                      </div>
                    </div>
                  </Card>
                  <p className="text-center mt-2 text-gray-600">{image.caption}</p>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="relative">
              <CarouselPrevious className="static translate-y-0 left-auto right-auto mr-2" />
              <CarouselNext className="static translate-y-0 left-auto right-auto" />
            </div>
          </Carousel>
        </TabsContent>
      </Tabs>

      <Separator className="my-6" />

      {/* Thumbnails */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" role="toolbar" aria-label="Image thumbnails">
        {images.map((image, index) => (
          <motion.button
            key={index}
            type="button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            className="cursor-pointer overflow-hidden rounded-md border border-gray-200 focus:outline-hidden focus:ring-2 focus:ring-primary"
            onClick={() => {
              const carouselContainer = document.querySelector('.embla__container');
              if (carouselContainer instanceof HTMLElement) {
                const scrollAmount = carouselContainer.clientWidth * index;
                console.warn("Thumbnail click: Embla API not directly accessible here for scroll.");
              }
            }}
            aria-label={`View image ${index + 1}: ${image.caption}`}
          >
            <img
              src={image.after}
              alt=""
              aria-hidden="true"
              className="w-full h-16 object-cover"
            />
          </motion.button>
        ))}
      </div>
    </div>
  );
}
